import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useState } from 'react';

import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { IntegrationSetupDialogProperties } from '../common';
import { IntegrationSetupDialogSkeleton } from '../shared/IntegrationSetupDialogSkeleton';
import {
  CheckrConnectionStatus,
  connectCheckr,
  disconnectCheckr
} from './service';

const NAME = 'Checkr';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

export type CheckrSetupDialogProps = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  status: CheckrConnectionStatus | undefined;
  loading: boolean;
  error: unknown;
  /** Re-fires the connection-status CF so both the card badge and the
   * dialog update after a save or disconnect. */
  onRefresh: () => void;
} & IntegrationSetupDialogProperties;

type NotConnectedFormProps = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onSaved: () => void;
};

const NotConnectedForm = ({
  open,
  onClose,
  orgId,
  onSaved
}: NotConnectedFormProps) => {
  const { t } = useUnsafeTranslation();

  const [apiKey, setApiKey] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canSave = !!apiKey.trim();

  const handleSave = () =>
    connectCheckr(orgId, apiKey.trim(), t)
      .then(() => {
        setErrorMessage(null);
        onSaved();
      })
      .catch((err: Error) => {
        setErrorMessage(err.message);
      });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('org.integrations.checkr.setupInfo.p1')}
        </Alert>

        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}

        <FormRow>
          <CustomInputLabel label={t('API Key')} fullWidth>
            <TextField
              variant="outlined"
              type="password"
              value={apiKey}
              onChange={(ev) => {
                setApiKey(ev.target.value);
                if (errorMessage) setErrorMessage(null);
              }}
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
      </Content>
      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!canSave}
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

type ConnectedStateFormProps = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onDisconnected: () => void;
};

const ConnectedStateForm = ({
  open,
  onClose,
  orgId,
  onDisconnected
}: ConnectedStateFormProps) => {
  const { t } = useUnsafeTranslation();

  const handleDisconnect = async () => {
    await disconnectCheckr(orgId);
    onDisconnected();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="success">{t('Connected')}</Alert>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={handleDisconnect}
            pending={`${t('Disconnecting')}...`}
          >
            {t('Disconnect')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

export const CheckrSetupDialog = ({
  open,
  onClose,
  onSuccessfulIntegration,
  orgId,
  status,
  loading,
  error,
  onRefresh
}: CheckrSetupDialogProps) => {
  if (loading && !status && !error) {
    return (
      <IntegrationSetupDialogSkeleton
        name={NAME}
        open={open}
        onClose={onClose}
      />
    );
  }

  const connected = status?.connected ?? false;

  if (!connected) {
    return (
      <NotConnectedForm
        open={open}
        onClose={onClose}
        orgId={orgId}
        onSaved={() => {
          onRefresh();
          onSuccessfulIntegration();
        }}
      />
    );
  }

  return (
    <ConnectedStateForm
      open={open}
      onClose={onClose}
      orgId={orgId}
      onDisconnected={onRefresh}
    />
  );
};
